import { useEffect, useMemo, useRef, useState } from "react";

import type { AssetListItem, RenderLogItem } from "@/lib/api";
import { api } from "@/lib/api";
import { formatDateTimeSec } from "@/lib/format";

interface RenderLogsModalProps {
  asset: AssetListItem;
  onClose: () => void;
}

/** Decisão nº 3 do produto: o administrador vê a razão técnica.
 *
 * Ela já viajava dentro do `metadata_json`, mas dentro de um JSON de vinte
 * linhas — que é o mesmo que não estar lá para quem precisa dela às pressas.
 * Estes são os campos sobre os quais se age: qual `rflow` recusou, como a
 * página foi tipada e o código que nomeia a capacidade que falta. O JSON
 * completo continua logo abaixo, porque nada aqui substitui o dado inteiro.
 */
const TECHNICAL_FIELDS = [
  { key: "rflow_version", label: "rflow" },
  { key: "terminal", label: "terminal" },
  { key: "reason", label: "razão" },
  { key: "code", label: "código" },
  { key: "phase", label: "fase" },
] as const;

function technicalChips(metadata: Record<string, unknown> | null | undefined) {
  if (!metadata) return [];
  return TECHNICAL_FIELDS.flatMap(({ key, label }) => {
    const value = metadata[key];
    if (value === null || value === undefined || value === "") return [];
    return [{ key, label, value: String(value) }];
  });
}

export function RenderLogsModal({ asset, onClose }: RenderLogsModalProps) {
  const overlayRef = useRef<HTMLDivElement>(null);
  const [logs, setLogs] = useState<RenderLogItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [level, setLevel] = useState<RenderLogItem["level"] | "all">("all");
  const [mode, setMode] = useState<"events" | "technical">("events");

  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  useEffect(() => {
    let cancelled = false;
    if (!asset.current_file_id) {
      setLoading(false);
      setLogs([]);
      return;
    }
    setLoading(true);
    setError(null);
    api
      .getRenderLogs(asset.current_file_id, { limit: 300 })
      .then((items) => {
        if (!cancelled) setLogs(items);
      })
      .catch((err: unknown) => {
        if (!cancelled) setError(err instanceof Error ? err.message : String(err));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [asset.current_file_id]);

  const filteredLogs = useMemo(() => {
    return logs.filter((item) => {
      if (level !== "all" && item.level !== level) return false;
      if (mode === "events") return !item.event.startsWith("technical.");
      return item.event.startsWith("technical.");
    });
  }, [level, logs, mode]);

  return (
    <div
      ref={overlayRef}
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4 backdrop-blur-sm"
      onMouseDown={(event) => {
        if (event.target === overlayRef.current) onClose();
      }}
    >
      <div className="flex max-h-[85vh] w-full max-w-5xl flex-col overflow-hidden rounded-xl border border-ink-700 bg-ink-900 shadow-2xl">
        <div className="flex items-center justify-between gap-3 border-b border-ink-700 px-5 py-4">
          <div className="min-w-0">
            <h2 className="text-sm font-semibold text-ink-100">Logs do Render</h2>
            <p className="truncate text-[11px] text-ink-400" title={asset.filename}>
              {asset.filename}
            </p>
          </div>
          <button
            type="button"
            aria-label="Fechar logs"
            onClick={onClose}
            className="shrink-0 rounded p-1 text-ink-400 hover:bg-ink-700 hover:text-ink-100"
          >
            <svg className="h-5 w-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} aria-hidden="true">
              <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <div className="flex items-center gap-3 border-b border-ink-700 px-5 py-3">
          <div className="inline-flex overflow-hidden rounded border border-ink-700 text-xs">
            <button
              type="button"
              className={`px-2 py-1 ${mode === "events" ? "bg-ink-700 text-ink-100" : "text-ink-300 hover:bg-ink-800"}`}
              onClick={() => setMode("events")}
            >
              Eventos
            </button>
            <button
              type="button"
              className={`px-2 py-1 ${mode === "technical" ? "bg-ink-700 text-ink-100" : "text-ink-300 hover:bg-ink-800"}`}
              onClick={() => setMode("technical")}
            >
              Técnico
            </button>
          </div>
          <select
            value={level}
            onChange={(event) => setLevel(event.target.value as RenderLogItem["level"] | "all")}
            className="rounded border border-ink-700 bg-ink-800 px-2 py-1 text-xs text-ink-200"
          >
            <option value="all">Todos níveis</option>
            <option value="debug">debug</option>
            <option value="info">info</option>
            <option value="warning">warning</option>
            <option value="error">error</option>
            <option value="critical">critical</option>
          </select>
        </div>

        <div className="flex-1 overflow-y-auto px-5 py-4 scrollbar-thin">
          {loading && <p className="text-sm text-ink-400">Carregando logs...</p>}
          {error && <p className="text-sm text-red-300">Erro ao carregar: {error}</p>}
          {!loading && !error && filteredLogs.length === 0 && (
            <p className="text-sm text-ink-500">Nenhum log encontrado para os filtros atuais.</p>
          )}
          {!loading && !error && filteredLogs.length > 0 && (
            <div className="space-y-2">
              {filteredLogs.map((entry) => (
                <article key={entry.id} className="rounded border border-ink-700 bg-ink-950/40 p-3">
                  <div className="mb-1 flex flex-wrap items-center gap-2 text-[11px] text-ink-400">
                    <span>{formatDateTimeSec(entry.occurred_at)}</span>
                    <span className="rounded bg-ink-800 px-1.5 py-0.5 text-[10px] text-ink-300">{entry.level}</span>
                    <span className="rounded bg-ink-800 px-1.5 py-0.5 text-[10px] text-ink-300">{entry.event}</span>
                    <span className="rounded bg-ink-800 px-1.5 py-0.5 text-[10px] text-ink-300">{entry.task_type}</span>
                  </div>
                  <p className="whitespace-pre-wrap break-words text-xs text-ink-200">{entry.message}</p>
                  {technicalChips(entry.metadata_json).length > 0 && (
                    <div className="mt-2 flex flex-wrap items-center gap-1.5">
                      {technicalChips(entry.metadata_json).map((chip) => (
                        <span
                          key={chip.key}
                          className={`rounded px-1.5 py-0.5 text-[10px] ${
                            entry.level === "error"
                              ? "bg-red-950/60 text-red-200"
                              : "bg-ink-800 text-ink-300"
                          }`}
                          title={`${chip.label}: ${chip.value}`}
                        >
                          <span className="text-ink-500">{chip.label}</span>{" "}
                          <span className="font-mono">{chip.value}</span>
                        </span>
                      ))}
                    </div>
                  )}
                  {entry.metadata_json && (
                    <pre className="mt-2 overflow-x-auto rounded bg-ink-900 p-2 text-[10px] text-ink-400">
                      {JSON.stringify(entry.metadata_json, null, 2)}
                    </pre>
                  )}
                </article>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
